<template>
  <li
    v-if="topicBundle"
    class="px-4 py-2 border-l-4 hover:bg-gray-200 group"
    :class="borderClass"
  >
    <div class="flex items-center">
      <fa
        :icon="statusIcon"
        fixed-width
        :class="statusClass"
        :title="$t(`components.landingpages.topicBundle.status.${bundleStatus}`)"
      />
      <div class="flex-1 px-4">
        {{ $t('components.landingpages.topicBundle.title', {
          published: topicBundle.publishedCount,
          expected: topicBundle.expectedCount
        }) }}
        <span class="ml-1 text-xs text-gray-500">
          {{ $t(`components.landingpages.topicBundle.status.${bundleStatus}`) }}
        </span>
      </div>
      <div class="flex justify-end space-x-1 w-20">
        <button
          class="btn-icon"
          :title="$t('components.landingpages.topicBundle.toggleDetail')"
          @click="isExpanded = !isExpanded"
        >
          <fa
            :icon="isExpanded ? faChevronUp : faChevronDown"
            fixed-width
          />
        </button>
        <button
          v-if="canApprove"
          class="btn-icon"
          :title="$t('components.landingpages.topicBundle.approve')"
          @click="approveBundle"
        >
          <fa
            :icon="faCheck"
            fixed-width
          />
        </button>
      </div>
    </div>

    <div
      v-if="isExpanded"
      class="mt-2 ml-8 text-xs text-gray-700"
    >
      <p
        v-if="!topicBundle.diagnosticsAvailable"
        class="text-yellow-700"
      >
        {{ $t('components.landingpages.topicBundle.diagnosticsUnavailable') }}
      </p>
      <p
        v-if="topicBundle.missingFields.length"
        class="text-yellow-700"
      >
        {{ $t('components.landingpages.topicBundle.missingData', {
          fields: topicBundle.missingFields.join(', ')
        }) }}
      </p>
      <p
        v-if="!topicBundle.approvable && blockedReason"
        class="text-yellow-700"
      >
        {{ blockedReason }}
      </p>
      <ul class="mt-1">
        <li
          v-for="member in topicBundle.members"
          :key="member.key"
          class="flex items-center py-1 space-x-2"
        >
          <span class="w-28">{{ memberLabel(member) }}</span>
          <span class="w-28">{{ $t(`components.landingpages.topicBundle.memberState.${member.state}`) }}</span>
          <span v-if="member.candidateVersion">
            {{ $t('components.landingpages.topicBundle.candidate', {
              version: member.candidateVersion,
              errors: member.quality?.errorCount || 0,
              warnings: member.quality?.warningCount || 0
            }) }}
          </span>
          <a
            v-if="member.candidateFile"
            :href="createReviewUrl(member.candidateFile)"
            target="_blank"
            class="btn-inline-small"
          >
            {{ $t('components.landingpages.topicBundle.reviewCandidate') }}
          </a>
          <span
            v-if="member.lastFailureCode"
            class="text-red-700"
          >
            {{ $t('components.landingpages.topicBundle.failure', {
              code: member.lastFailureCode,
              message: member.lastFailureMessage || '-'
            }) }}
          </span>
        </li>
      </ul>
    </div>
  </li>
</template>

<script>
import {
  faCheck,
  faChevronDown,
  faChevronUp,
  faClock,
  faExclamationTriangle,
  faSpinner
} from '@fortawesome/free-solid-svg-icons'
import eventBus, { eventNames } from '@/lib/eventBus'
import role from '@/mixins/role'
import APPROVE_BUNDLE from '@/graphql/ApproveAndPublishMaterialBundle.gql'
import GET_TOPIC_BUNDLE from '@/graphql/GetTopicMaterialBundle.gql'
import MATERIALS from '@/graphql/material/materials.gql'

const ICONS_BY_STATUS = {
  PUBLISHED: faCheck,
  READY: faCheck,
  GENERATING: faSpinner,
  BLOCKED: faExclamationTriangle,
  FAILED: faExclamationTriangle,
  PENDING: faClock
}

const CLASSES_BY_STATUS = {
  PUBLISHED: 'text-green-500',
  READY: 'text-blue-500',
  GENERATING: 'animate-spin',
  BLOCKED: 'text-yellow-500',
  FAILED: 'text-red-500',
  PENDING: 'text-gray-400'
}

const BORDERS_BY_STATUS = {
  PUBLISHED: 'border-green-400',
  READY: 'border-blue-400',
  GENERATING: 'border-gray-300',
  BLOCKED: 'border-yellow-400',
  FAILED: 'border-red-400',
  PENDING: 'border-gray-300'
}

export default {
  mixins: [role],

  props: {
    companyId: { type: String, required: true },
    landingpageId: { type: String, required: true }
  },

  data () {
    return {
      topicBundle: null,
      isExpanded: false,
      faCheck,
      faChevronDown,
      faChevronUp
    }
  },

  apollo: {
    topicBundle: {
      query: GET_TOPIC_BUNDLE,
      variables () {
        return { companyId: this.companyId, landingpageId: this.landingpageId }
      },
      fetchPolicy: 'cache-and-network'
    }
  },

  computed: {
    bundleStatus () {
      if (this.topicBundle.complete) return this.topicBundle.replacementInFlight ? 'GENERATING' : 'PUBLISHED'
      if (this.topicBundle.approvable) return 'READY'
      if (this.topicBundle.failedMembers.length) return 'FAILED'
      if (this.topicBundle.blockedMembers.length) return 'BLOCKED'
      if (this.topicBundle.pendingReviewMembers.length) return 'READY'
      return 'PENDING'
    },
    statusIcon () {
      return ICONS_BY_STATUS[this.bundleStatus]
    },
    statusClass () {
      return CLASSES_BY_STATUS[this.bundleStatus]
    },
    borderClass () {
      return BORDERS_BY_STATUS[this.bundleStatus]
    },
    canApprove () {
      return this.hasRole(this.ROLES.SUPER_ADMIN) && this.topicBundle.approvable
    },
    candidates () {
      return this.topicBundle.members.map(member => ({
        materialId: member.materialId,
        versionNumber: member.candidateVersion,
        checksumSha256: member.candidateChecksum
      }))
    },
    blockedReason () {
      const memberKeys = [
        ...this.topicBundle.missingMembers,
        ...this.topicBundle.incompleteMembers,
        ...this.topicBundle.failedMembers,
        ...this.topicBundle.pendingReviewMembers
      ]
      return memberKeys.length
        ? this.$t('components.landingpages.topicBundle.unavailable', { members: memberKeys.join(', ') })
        : ''
    }
  },

  methods: {
    memberLabel (member) {
      return member.displayName || member.key
    },
    createReviewUrl ({ cdnUrl }) {
      const url = new URL(cdnUrl)
      url.searchParams.append('attachment', 'false')
      return url.toString()
    },
    refetchAfterBundleChange () {
      return [
        { query: MATERIALS, variables: { companyId: this.companyId } },
        { query: GET_TOPIC_BUNDLE, variables: { companyId: this.companyId, landingpageId: this.landingpageId } }
      ]
    },
    async approveBundle () {
      try {
        await this.$apollo.mutate({
          mutation: APPROVE_BUNDLE,
          variables: {
            input: {
              companyId: this.companyId,
              landingpageId: this.landingpageId,
              candidates: this.candidates
            }
          },
          refetchQueries: this.refetchAfterBundleChange()
        })
        eventBus.$emit(eventNames.MATERIAL_WORK_STARTED, { companyId: this.companyId })
      } catch (error) {
        alert(this.$t('components.landingpages.topicBundle.approveFailed', { error: error.message }))
      }
    }
  }
}
</script>
